import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';

import wordmarkStacked from '@/assets/logo/wordmark-stacked.svg';
import { useAuthStore } from '@/stores/auth-store';
import { authService } from '@/services/auth-service';
import { Button } from '@/components/common/Button';
import { Input } from '@/components/common/Input';
import { validateRedirectPath } from '@/utils/redirect-validation';

type Mode = 'signin' | 'signup' | 'magic-link';

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
      <path d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332C2.438 15.983 5.482 18 9 18z" fill="#34A853"/>
      <path d="M3.964 10.707c-.18-.54-.282-1.117-.282-1.707s.102-1.167.282-1.707V4.961H.957C.347 6.175 0 7.55 0 9s.348 2.825.957 4.039l3.007-2.332z" fill="#FBBC05"/>
      <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0 5.482 0 2.438 2.017.957 4.961L3.964 7.293C4.672 5.166 6.656 3.58 9 3.58z" fill="#EA4335"/>
    </svg>
  );
}

export function AuthPage() {
  const { signIn, signUp, signInWithMagicLink } = useAuthStore();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  /**
   * Invite-redemption intent (issue #697).
   *
   * A first-time beta invitee arrives at `/auth?redirect=/redeem?token=…&email=…`
   * via the `/invite/:token` CTA. The common precondition is that the invited
   * email has NO existing Ridgen account yet — so the screen must default to
   * Create account, not Sign in.
   *
   * Intent is inferred from the redirect *shape* alone: `validateRedirectPath`
   * (the open-redirect guard) resolves a valid invite redirect to a
   * `/redeem?token=<uuid>` string. No new query param is introduced. The masked
   * `?email=` hint (e.g. "t***@example.com") is display-only — it tells the
   * recipient which address the new account must use, without unmasking it (C9).
   */
  const safeRedirect = validateRedirectPath(searchParams.get('redirect'));
  const isInviteRedemption = safeRedirect.startsWith('/redeem?token=');
  const invitedEmailHint = isInviteRedemption ? searchParams.get('email') : null;

  const [mode, setMode] = useState<Mode>(isInviteRedemption ? 'signup' : 'signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Show error if OAuth flow failed and redirected back with ?error=oauth_failed
  useEffect(() => {
    if (searchParams.get('error') === 'oauth_failed') {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- one-shot sync from external prop on mount
      setError('Google sign-in was cancelled or failed. Please try again.');
    }
  }, [searchParams]);

  /**
   * Post-auth navigation (M58 / consensus-reviewer C2).
   *
   * The M58 invite flow lands users at `/auth?redirect=/redeem?token=<uuid>`
   * when they need to sign in to redeem. After auth resolves, the browser URL
   * is still `/auth?redirect=…`, which the AppShell catch-all route would
   * otherwise redirect to `/` (losing the invite token).
   *
   * `validateRedirectPath` is the open-redirect guard — it accepts only
   * `/invite/<uuid>` and `/redeem?token=<uuid>` shapes; any other value
   * (path traversal, absolute URL, malformed UUID) falls back to `/`.
   */
  function navigateAfterAuth(): void {
    navigate(safeRedirect, { replace: true });
  }

  function resetForm() {
    setEmail('');
    setPassword('');
    setDisplayName('');
    setError(null);
    setSuccessMessage(null);
  }

  function switchMode(next: Mode) {
    resetForm();
    setMode(next);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);
    setIsSubmitting(true);

    try {
      if (mode === 'signin') {
        await signIn(email, password);
        // Successful sign-in: AuthGuard will swap to AppShell. Navigate to the
        // validated `?redirect=` target so M58 invite redemption can resume.
        navigateAfterAuth();
      } else if (mode === 'signup') {
        // Pass ?redirect= through sign-up so the email-confirmation click can
        // resume the invite flow (F12.1 Path 1). The validated path is threaded
        // into emailRedirectTo inside auth-service.signUp.
        await signUp(email, password, displayName, searchParams.get('redirect'));
        // Successful sign-up: same redirect handling as sign-in.
        navigateAfterAuth();
      } else {
        // Pass ?redirect= through the magic-link so AuthCallbackPage can resume
        // the invite flow after the user clicks the link in their email.
        await signInWithMagicLink(email, searchParams.get('redirect'));
        setSuccessMessage('Check your email — a magic link is on its way.');
        // Magic link path: no immediate navigation — user clicks link in email,
        // which routes through /auth/callback → AuthCallbackPage handles redirect.
      }
    } catch (err) {
      if (err instanceof Error && err.message === 'EMAIL_CONFIRMATION_REQUIRED') {
        // Not an error — Supabase sent a confirmation email. Show success message.
        setSuccessMessage('Account created! Check your email and click the confirmation link to sign in.');
      } else {
        setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleGoogleSignIn() {
    setError(null);
    setIsGoogleLoading(true);
    try {
      // Pass ?redirect= through Google OAuth so AuthCallbackPage can resume
      // the invite flow after Google OAuth resolves.
      await authService.signInWithGoogle(searchParams.get('redirect'));
      // OAuth redirect happens — page will navigate away
    } catch {
      setError('Google sign-in failed. Please try again.');
      setIsGoogleLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-[var(--color-bg)] flex flex-col items-center justify-center px-4 py-12">
      {/* Logo / brand — logo → tagline (heading removed — logo wordmark is the sole Ridgen representation) */}
      <div className="mb-8 text-center flex flex-col items-center gap-3" data-testid="auth-brand-block">
        <img
          src={wordmarkStacked}
          alt="Ridgen"
          className="h-24 w-auto"
          data-testid="auth-logo"
          data-size="xl"
        />
        <p className="text-body-sm text-[var(--color-text-muted)]" data-testid="auth-tagline">Know your land. Own your hunt.</p>
      </div>

      {/* Card */}
      <div
        data-testid="auth-card"
        className="w-full max-w-sm bg-bark-800 border-[0.5px] border-bark-600 rounded-xl shadow-card-lg backdrop-blur-sm p-6"
      >
        {/* Mode tabs */}
        {mode !== 'magic-link' && (
          <div className="flex gap-1 bg-[var(--color-bg)] rounded-lg p-1 mb-6">
            <button
              type="button"
              data-testid="auth-mode-signin-tab"
              onClick={() => switchMode('signin')}
              className={`flex-1 py-1.5 rounded-md text-body-sm font-medium transition-colors duration-100 ${
                mode === 'signin'
                  ? 'bg-[var(--color-elevated)] text-[var(--color-text-primary)]'
                  : 'text-[var(--color-text-muted)] hover:text-[var(--color-text-secondary)]'
              }`}
            >
              Sign in
            </button>
            <button
              type="button"
              data-testid="auth-mode-signup-tab"
              onClick={() => switchMode('signup')}
              className={`flex-1 py-1.5 rounded-md text-body-sm font-medium transition-colors duration-100 ${
                mode === 'signup'
                  ? 'bg-[var(--color-elevated)] text-[var(--color-text-primary)]'
                  : 'text-[var(--color-text-muted)] hover:text-[var(--color-text-secondary)]'
              }`}
            >
              Create account
            </button>
          </div>
        )}

        {/* Invite-redemption hint (issue #697) — shown when the user arrived from
            a validated `/redeem?token=` invite redirect. Communicates that an
            account must be created with the invited email (or sign in if they
            already have one). The masked hint (e.g. "t***@example.com") is
            display-only and never unmasked (C9). Hidden in magic-link mode,
            which has its own dedicated header. */}
        {isInviteRedemption && mode !== 'magic-link' && (
          <p
            className="mb-6 text-body-sm text-[var(--color-text-muted)]"
            data-testid="auth-invite-hint"
          >
            {invitedEmailHint ? (
              <>
                You&rsquo;re redeeming a Ridgen Beta invite for{' '}
                <span className="font-medium text-[var(--color-text-primary)]">
                  {invitedEmailHint}
                </span>
                . Create an account with that email to finish — or sign in if you
                already have one.
              </>
            ) : (
              <>
                You&rsquo;re redeeming a Ridgen Beta invite. Create an account with
                the invited email to finish — or sign in if you already have one.
              </>
            )}
          </p>
        )}

        {mode === 'magic-link' && (
          <div className="mb-6">
            <button
              type="button"
              onClick={() => switchMode('signin')}
              className="flex items-center gap-1.5 text-body-sm text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] transition-colors"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="15 18 9 12 15 6" />
              </svg>
              Back to sign in
            </button>
            <h2 className="mt-3 font-display text-display-sm text-[var(--color-text-display)]">Magic link</h2>
            <p className="mt-1 text-body-sm text-[var(--color-text-muted)]">
              Enter your email and we&apos;ll send a one-click sign-in link.
            </p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          {mode === 'signup' && (
            <Input
              label="Display name"
              id="displayName"
              value={displayName}
              onChange={setDisplayName}
              placeholder="Your name"
              required
            />
          )}

          <Input
            label="Email"
            id="email"
            type="email"
            value={email}
            onChange={setEmail}
            placeholder="you@example.com"
            required
            data-testid="auth-email-input"
          />

          {mode !== 'magic-link' && (
            <Input
              label="Password"
              id="password"
              type="password"
              value={password}
              onChange={setPassword}
              placeholder="••••••••"
              required
              data-testid="auth-password-input"
            />
          )}

          {error && (
            <p className="text-body-sm text-red-400 bg-red-950/40 border border-red-800/50 rounded-md px-3 py-2">
              {error}
            </p>
          )}

          {successMessage && (
            <p className="text-body-sm text-[var(--color-accent)] bg-[var(--color-accent-subtle)] border border-[var(--color-accent)]/30 rounded-md px-3 py-2">
              {successMessage}
            </p>
          )}

          <Button
            type="submit"
            variant="tactical"
            data-testid={mode === 'signin' ? 'auth-signin-button' : mode === 'signup' ? 'auth-signup-button' : 'auth-magic-link-button'}
            disabled={isSubmitting || isGoogleLoading}
            className="w-full mt-1"
          >
            {isSubmitting
              ? 'Please wait…'
              : mode === 'signin'
                ? 'Sign in'
                : mode === 'signup'
                  ? 'Create account'
                  : 'Send magic link'}
          </Button>
        </form>

        {/* Divider */}
        <div className="relative my-4">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-[var(--color-border)]" />
          </div>
          <div className="relative flex justify-center text-body-sm">
            <span className="bg-bark-800 px-2 text-[var(--color-text-muted)]">or</span>
          </div>
        </div>

        {/* Google sign-in */}
        <Button
          type="button"
          variant="ghost"
          data-testid="auth-google-button"
          onClick={() => void handleGoogleSignIn()}
          disabled={isSubmitting || isGoogleLoading}
          className="w-full bg-bark-700 border border-bark-600"
        >
          <GoogleIcon />
          <span className="ml-3">{isGoogleLoading ? 'Redirecting…' : 'Continue with Google'}</span>
        </Button>

        {/* Magic link option — only shown on sign in / sign up */}
        {mode !== 'magic-link' && (
          <div className="mt-5 text-center">
            <button
              type="button"
              onClick={() => switchMode('magic-link')}
              className="text-body-sm text-[var(--color-text-muted)] hover:text-[var(--color-accent)] transition-colors duration-100"
            >
              Sign in with a magic link instead
            </button>
          </div>
        )}
      </div>

      {/* Legal acknowledgment — present in all auth modes. text-bark-400 keeps it
          unobtrusive (intentionally NOT the muted token, which would be more
          prominent). */}
      <p
        className="mt-6 max-w-sm text-center text-body-xs text-bark-400"
        data-testid="auth-legal-acknowledgment"
      >
        By signing in, you agree to our{' '}
        <Link to="/terms" className="underline hover:text-bark-200 transition-colors" data-testid="auth-terms-link">
          Terms of Service
        </Link>{' '}
        and{' '}
        <Link to="/privacy" className="underline hover:text-bark-200 transition-colors" data-testid="auth-privacy-link">
          Privacy Policy
        </Link>
        .
      </p>
    </div>
  );
}
